/**
 * Saving a report as a file, so it can leave the machine that made it.
 *
 * Until now a stored report could be opened and deleted, and that was all. The
 * whole point of a report is that somebody else reads it — a colleague, and
 * above all the client it describes — and neither could, because the library
 * lives in one person's browser. A downloaded file is the cheapest thing that
 * gets it out: it attaches to an email, it survives a cleared cache, it lands
 * on a shared drive.
 *
 * A self-contained HTML file, not a PDF. The report is already complete HTML
 * with its styles inline, so it opens in any browser exactly as it did here,
 * with its tables selectable and its numbers copyable. Rendering it to PDF
 * would need a headless browser we do not run, and would take those away.
 *
 * @module services/downloadReport
 */

/**
 * Turn a report title into something a filesystem and a mail client will both
 * accept, without flattening it into nonsense.
 *
 * Thai is kept: the titles are Thai, every OS this product's users run handles
 * Thai filenames, and transliterating would produce a name nobody recognises.
 * What goes are the characters that actually break things — path separators,
 * the Windows reserved set, and control codes.
 */
export const reportFileName = (title: string, createdAt: string): string => {
  const safe = (title || "report")
    .replace(/[\\/:*?"<>|]/g, " ")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80) || "report";

  // The date the report describes, not the date it was downloaded: two
  // downloads of the same report must produce the same file.
  const stamp = (() => {
    const d = new Date(createdAt);
    return Number.isNaN(d.getTime()) ? "undated" : d.toISOString().slice(0, 10);
  })();

  return `${safe} — ${stamp}.html`;
};

/**
 * Save a report's HTML to the viewer's downloads.
 *
 * @param html - the complete document, exactly as the library stores it
 * @param title - the report's title, used for the filename
 * @param createdAt - ISO timestamp, used for the filename's date
 */
export const downloadReportHtml = (html: string, title: string, createdAt: string): void => {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = reportFileName(title, createdAt);
  // Appended before clicking: Firefox ignores a click on a link that is not in
  // the document, and does so silently.
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Long enough for the download to start, which is all the URL is needed for.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export default downloadReportHtml;
